// Helpers de horário de funcionamento compartilhados entre a tela de horários
// (pages/horario/Lista.vue) e o pré-visualizador semanal
// (components/horario/PreviewSemanaHorarios.vue).
//
// Regra base do produto: quando o horário de fim é menor que o de início, o
// turno atravessa a meia-noite e termina no dia seguinte.

export const MINUTOS_DIA = 1440;
export const MINUTOS_SEMANA = MINUTOS_DIA * 7;

// Ordem da semana usada no formulário e no calendário (domingo → sábado).
export const DIAS_SEMANA = [
  { dia_semana: 0, nome: 'Domingo', curto: 'Dom' },
  { dia_semana: 1, nome: 'Segunda', curto: 'Seg' },
  { dia_semana: 2, nome: 'Terça', curto: 'Ter' },
  { dia_semana: 3, nome: 'Quarta', curto: 'Qua' },
  { dia_semana: 4, nome: 'Quinta', curto: 'Qui' },
  { dia_semana: 5, nome: 'Sexta', curto: 'Sex' },
  { dia_semana: 6, nome: 'Sábado', curto: 'Sáb' },
];

// Cria os 7 dias já com os 4 campos de horário declarados. Declarar as chaves
// desde o início é o que mantém o formulário reativo no Vue 2 — sem isso, os
// campos vindos da API entram no objeto sem observabilidade e o
// pré-visualizador não acompanha a digitação.
export function criarDiasSemana () {
  return DIAS_SEMANA.map(dia => ({
    ...dia,
    horario_inicio_1: null,
    horario_fim_1: null,
    horario_inicio_2: null,
    horario_fim_2: null,
  }));
}

// "19:05" → 1145. Retorna null quando o valor está vazio ou inválido.
export function paraMinutos (hora) {
  if (!hora || typeof hora !== 'string') return null;
  const partes = hora.split(':');
  if (partes.length < 2) return null;
  const h = parseInt(partes[0], 10);
  const m = parseInt(partes[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

// 1145 → "19:05". Aceita valores acima de 1440 (1470 → "00:30").
export function paraHora (minutos) {
  const total = ((Math.round(minutos) % MINUTOS_DIA) + MINUTOS_DIA) % MINUTOS_DIA;
  const h = String(Math.floor(total / 60)).padStart(2, '0');
  const m = String(total % 60).padStart(2, '0');
  return `${h}:${m}`;
}

// 325 → "5h25". 45 → "45min".
export function formatarDuracao (minutos) {
  const total = Math.max(0, Math.round(minutos || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}min`;
  if (!m) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}`;
}

// Mesma conta usada na validação do formulário: fim menor que início soma 24h.
export function duracaoTurno (inicio, fim) {
  const i = paraMinutos(inicio);
  const f = paraMinutos(fim);
  if (i === null || f === null) return 0;
  let diff = f - i;
  if (diff < 0) diff += MINUTOS_DIA;
  return diff;
}

export function atravessaMeiaNoite (inicio, fim) {
  const i = paraMinutos(inicio);
  const f = paraMinutos(fim);
  if (i === null || f === null) return false;
  return f < i;
}

// Turno só conta quando tem os dois campos e pelo menos 1 minuto de duração.
export function turnoValido (inicio, fim) {
  return duracaoTurno(inicio, fim) >= 1;
}

// Intervalos dos turnos de um dia, ancorados na meia-noite do próprio dia — um
// turno que vira a noite termina depois de 1440.
export function intervalosDia (dia) {
  if (!dia) return [];
  return [1, 2]
    .filter(turno => turnoValido(dia[`horario_inicio_${turno}`], dia[`horario_fim_${turno}`]))
    .map(turno => {
      const de = paraMinutos(dia[`horario_inicio_${turno}`]);
      return { de, ate: de + duracaoTurno(dia[`horario_inicio_${turno}`], dia[`horario_fim_${turno}`]) };
    });
}

// Minutos abertos nos turnos do dia, com a sobreposição entre turno 1 e turno 2
// contada uma vez só.
export function minutosAbertosDia (dia) {
  return minutosCobertos(intervalosDia(dia));
}

export function diaTemTurno (dia) {
  if (!dia) return false;
  return turnoValido(dia.horario_inicio_1, dia.horario_fim_1)
      || turnoValido(dia.horario_inicio_2, dia.horario_fim_2);
}

// Junta intervalos `{ de, ate }` que se cruzam ou encostam num só. Base de todo
// cálculo de tempo aberto: somar os turnos direto contaria duas vezes o trecho
// em que eles se sobrepõem, e um dia de 24h chegava a marcar 36h.
export function unirIntervalos (intervalos) {
  const uniao = [];

  [...intervalos]
    .sort((a, b) => a.de - b.de)
    .forEach(faixa => {
      const ultimo = uniao[uniao.length - 1];
      if (ultimo && faixa.de <= ultimo.ate) {
        ultimo.ate = Math.max(ultimo.ate, faixa.ate);
      } else {
        uniao.push({ de: faixa.de, ate: faixa.ate });
      }
    });

  return uniao;
}

// Minutos cobertos por uma lista de intervalos, sem contar sobreposição duas vezes.
export function minutosCobertos (intervalos) {
  return unirIntervalos(intervalos).reduce((total, faixa) => total + (faixa.ate - faixa.de), 0);
}

// Minutos abertos na semana. Turnos sobrepostos contam uma vez só, então o
// total nunca passa de 168h.
export function totalMinutosSemana (dias) {
  if (!Array.isArray(dias) || !dias.length) return 0;
  return minutosCobertos(intervalosAbsolutos(dias));
}

// Transforma os dias do formulário nos blocos desenhados no calendário.
// Um turno que fecha depois da meia-noite vira dois blocos: o trecho até 24:00
// na coluna do próprio dia e a continuação a partir de 00:00 na coluna do dia
// seguinte (o sábado transborda para o domingo).
export function montarBlocosSemana (dias) {
  const blocos = [];
  if (!Array.isArray(dias) || !dias.length) return blocos;

  dias.forEach((dia, index) => {
    [1, 2].forEach(turno => {
      const inicio = dia[`horario_inicio_${turno}`];
      const fim = dia[`horario_fim_${turno}`];
      if (!turnoValido(inicio, fim)) return;

      const proximo = (index + 1) % dias.length;
      const base = {
        turno,
        inicio,
        fim,
        duracao: duracaoTurno(inicio, fim),
        atravessa: atravessaMeiaNoite(inicio, fim),
        diaOrigem: index,
        nomeOrigem: dia.nome,
        nomeSeguinte: dias[proximo].nome,
      };

      const de = paraMinutos(inicio);
      const ate = paraMinutos(fim);

      if (!base.atravessa) {
        blocos.push({ ...base, id: `${index}-${turno}`, coluna: index, de, ate });
        return;
      }

      blocos.push({ ...base, id: `${index}-${turno}-a`, coluna: index, de, ate: MINUTOS_DIA, parte: 'inicio' });
      if (ate > 0) {
        blocos.push({ ...base, id: `${index}-${turno}-b`, coluna: proximo, de: 0, ate, parte: 'fim' });
      }
    });
  });

  return blocos;
}

// Intervalos de cada turno em minutos absolutos da semana, já quebrados quando
// passam do sábado para o domingo. Usado para detectar sobreposições.
function intervalosAbsolutos (dias) {
  const intervalos = [];

  dias.forEach((dia, index) => {
    [1, 2].forEach(turno => {
      const inicio = dia[`horario_inicio_${turno}`];
      const fim = dia[`horario_fim_${turno}`];
      if (!turnoValido(inicio, fim)) return;

      const de = index * MINUTOS_DIA + paraMinutos(inicio);
      const ate = de + duracaoTurno(inicio, fim);
      const ref = { turno, nome: dia.nome, indice: index };

      if (ate <= MINUTOS_SEMANA) {
        intervalos.push({ ...ref, de, ate });
      } else {
        intervalos.push({ ...ref, de, ate: MINUTOS_SEMANA });
        intervalos.push({ ...ref, de: 0, ate: ate - MINUTOS_SEMANA });
      }
    });
  });

  return intervalos;
}

// Aponta turnos que se cruzam na linha do tempo — inclusive a madrugada de um
// dia batendo no turno do dia seguinte, que é o erro mais comum de quem
// configura horários que passam da meia-noite.
export function detectarSobreposicoes (dias) {
  if (!Array.isArray(dias) || !dias.length) return [];

  const intervalos = intervalosAbsolutos(dias);
  const conflitos = [];
  const vistos = {};

  for (let i = 0; i < intervalos.length; i++) {
    for (let j = i + 1; j < intervalos.length; j++) {
      const a = intervalos[i];
      const b = intervalos[j];
      if (a.indice === b.indice && a.turno === b.turno) continue;
      if (a.de >= b.ate || b.de >= a.ate) continue;

      const chave = [`${a.indice}-${a.turno}`, `${b.indice}-${b.turno}`].sort().join('|');
      if (vistos[chave]) continue;
      vistos[chave] = true;
      conflitos.push({ a: { nome: a.nome, turno: a.turno }, b: { nome: b.nome, turno: b.turno } });
    }
  }

  return conflitos;
}
